import { Message } from 'ably';
import { useLocation } from 'react-router-dom';
import { QueueTab, useAlert } from '@gv/triage-components';
import { AblyChannelName, AblyMessageEvent } from '@gv/triage-components';
import {
  useRef,
  useState,
  RefObject,
  useEffect,
  useContext,
  useCallback,
} from 'react';

import { Routes } from 'config';
import { useAppSelector } from 'store';
import { selectAuthForGVTalk } from 'store/slices/auth';
import { UserActionsContext } from 'context/user-actions';
import { useAblyEvent, useAblyChannel, useAblyEventChannel } from 'hooks';
import {
  selectActionQueue,
  selectCurrentTask,
  selectCurrentTaskId,
} from 'store/api/action-queue/selectors';

import { Audio } from './styles';
import { NotifierProps } from './types';
import { AlertMessage } from './alertMessage';

const audioURL =
  'https://guardianvet-prod-files.s3.us-east-2.amazonaws.com/new-call-sound.mp3';
const newMessageAudioURL =
  'https://guardianvet-dev-files.s3.us-east-2.amazonaws.com/1586437343050notification_sound.mp3.mp3';

interface NotificationItem {
  id: string;
  body: string;
  icon: string;
  chatId: string;
  timestamp: number;
  senderName: string;
  senderRole: string;
  clickAction: string;
}

export const Notifier = ({ user, isOnCall }: NotifierProps): JSX.Element => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const msgAudioRef = useRef<HTMLAudioElement>(null);
  const { isUserClickHappened } = useContext(UserActionsContext);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const timersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const { showSuccessAlert } = useAlert();
  const location = useLocation();
  const currentTaskId = useAppSelector(selectCurrentTaskId);
  const currentTask = useAppSelector(selectCurrentTask);

  const stateRef = useRef({
    currentTask,
    taskId: currentTaskId,
    path: location.pathname,
  });

  useEffect(() => {
    stateRef.current = {
      currentTask,
      taskId: currentTaskId,
      path: location.pathname,
    };
  }, [location.pathname, currentTaskId, currentTask]);

  const queueItems = useAppSelector(selectActionQueue(QueueTab.Queue));
  const queueCount = queueItems.length;

  const queueCountRef = useRef(queueCount);

  const playRef = useCallback(
    (ref: RefObject<HTMLAudioElement>, message: string) => {
      if (!ref.current || !isUserClickHappened) {
        return;
      }
      const player = ref.current;
      player.pause();
      player.currentTime = 0;
      if (isOnCall) {
        showSuccessAlert(message);
      } else {
        try {
          player.play();
        } catch (error) {
          // Silently handle errors
        }
      }
    },
    [isOnCall, showSuccessAlert, isUserClickHappened]
  );

  const playNotif = useCallback(
    () => playRef(audioRef, 'New Call/Chat is in queue'),
    [playRef]
  );

  const playMessageNotif = useCallback(
    () => playRef(msgAudioRef, 'New Message Received'),
    [playRef]
  );

  useEffect(() => {
    if (queueCount > queueCountRef.current) {
      playNotif();
    }
    queueCountRef.current = queueCount;
  }, [queueCount, playNotif]);

  const onNewMessage = useCallback(
    (message: any) => {
      const { channelName } = message;
      const { path, taskId } = stateRef.current;
      if (channelName) {
        if (
          channelName !== taskId ||
          !path.includes(Routes.ActionCenter.Comm)
        ) {
          playMessageNotif();
        }
      }
    },
    [playMessageNotif]
  );

  useAblyEvent(`incoming-message:${user.id}`, onNewMessage);

  // TODO: handle this in future
  // if (prevProps.newCheckInCount < newCheckInCount) {
  //   playNotif();
  // }

  const gvTalkAuth = useAppSelector(selectAuthForGVTalk);

  // Listen to Ably message events for chat messages (similar to unread count)
  const chatChannel = useAblyChannel(AblyChannelName.Chat);

  const onChatMessageCreated = useCallback(
    async (msg: Message) => {
      const { data } = msg;
      const { chatId, isPublic, profileId, notifyIds, id: messageId } = data;

      // Use authProfileId (same as useMessages hook does)
      const authProfileId = gvTalkAuth.authProfileId;

      // Don't show notification if user is not authenticated
      if (!authProfileId) {
        return;
      }

      // Don't show notification if it's from the current user
      if (profileId === authProfileId) {
        return;
      }

      // Check if this message is for the current user
      // Similar to how useMessages checks: (isPublic || notifyIds.includes(authProfileId))
      const isForCurrentUser =
        isPublic || (notifyIds && notifyIds.includes(authProfileId));

      // Only show notification if it's for the current user
      if (!isForCurrentUser) {
        return;
      }

      try {
        // Fetch message details to get sender info
        const { api } = await import('api');
        const response = await api.gvMessages.getGvMessage({
          chatId,
          messageId,
        });
        const message = response.data.data;

        // Get sender info from message
        const sender = message?.author?.user;
        const senderName = sender?.name || 'Unknown';
        const senderRole = sender?.role || '';
        const senderAvatar = sender?.profile_pic || '';

        // Get message text
        const messageText =
          message?.formatting?.ops[0]?.insert || 'New message';

        // Add new notification to the array instead of replacing
        const newNotification: NotificationItem = {
          senderRole,
          senderName,
          clickAction: '',
          body: messageText,
          icon: senderAvatar,
          timestamp: Date.now(),
          chatId: String(chatId),
          id: `msg-${Date.now()}-${Math.random()}`,
        };

        setNotifications((prev) => [...prev, newNotification]);
        playMessageNotif();

        // Set auto-close timer for 2 minutes (120000ms)
        const timer = setTimeout(() => {
          setNotifications((prev) =>
            prev.filter((notif) => notif.id !== newNotification.id)
          );
          timersRef.current.delete(newNotification.id);
        }, 120000); // 2 minutes

        timersRef.current.set(newNotification.id, timer);
      } catch (error) {
        // Silently handle errors
      }
    },
    [gvTalkAuth, playMessageNotif]
  );

  useAblyEventChannel(
    AblyMessageEvent.Created,
    onChatMessageCreated,
    chatChannel
  );

  // Listen to reaction events to notify users when someone reacts to their messages
  const onChatMessageReacted = useCallback(
    async (msg: Message) => {
      const { data } = msg;
      const {
        chatId,
        isPublic,
        notifyIds,
        id: messageId,
        profileId: reactorProfileId,
      } = data;

      // Use authProfileId (same as useMessages hook does)
      const authProfileId = gvTalkAuth.authProfileId;

      // Don't show notification if user is not authenticated
      if (!authProfileId) {
        return;
      }

      // Don't show notification if it's from the current user (they're reacting to their own message)
      if (reactorProfileId === authProfileId) {
        return;
      }

      // Check if this message is for the current user
      // Similar to how useMessages checks: (isPublic || notifyIds.includes(authProfileId))
      const isForCurrentUser =
        isPublic || (notifyIds && notifyIds.includes(authProfileId));

      // Only show notification if it's for the current user
      if (!isForCurrentUser) {
        return;
      }

      // Check if this reaction is for a message that belongs to the current user
      // We need to fetch the message to see who the author is
      try {
        const { api } = await import('api');
        const response = await api.gvMessages.getGvMessage({
          chatId,
          messageId,
        });
        const message = response.data.data;

        // Get message author ID directly from message.authorId (profile ID)
        const messageAuthorId = message?.authorId;

        // Only show notification if the reaction is on the current user's message
        // message.authorId is the profile ID of the message author
        if (messageAuthorId === authProfileId) {
          return;
        }

        // Check if user is already viewing this chat
        const { path, taskId } = stateRef.current;
        if (path.includes(Routes.ActionCenter.Comm) && taskId) {
          // User is in comm view, might be viewing this chat
          return;
        }

        // Get the latest reaction from the reactions array
        // reactions array structure: [{ id, reaction, authorId, author: { user: {...} } }]
        if (!message?.reactions || message.reactions.length === 0) {
          return;
        }

        const latestReaction = message.reactions[message.reactions.length - 1];

        // Get reactor info from reaction.author.user (based on API structure)
        const reactor =
          latestReaction?.author?.user || latestReaction?.author?.client;
        const reactorName = reactor?.name || 'Someone';
        const reactorAvatar = reactor?.profile_pic || reactor?.avatar || '';

        // Get reaction emoji
        const reactionEmoji = latestReaction?.reaction || '';

        // Get message text preview
        const messageText =
          message?.formatting?.ops?.[0]?.insert ||
          message?.text ||
          'your message';

        const previewText = `${reactorName} ${reactionEmoji} reacted to your message: "${messageText.substring(0, 50)}${messageText.length > 50 ? '...' : ''}"`;

        // Add new notification to the array instead of replacing
        const newNotification: NotificationItem = {
          senderRole: '',
          clickAction: '',
          body: previewText,
          icon: reactorAvatar,
          timestamp: Date.now(),
          chatId: String(chatId),
          senderName: reactorName,
          id: `reaction-${Date.now()}-${Math.random()}`,
        };

        setNotifications((prev) => [...prev, newNotification]);
        playMessageNotif();

        // Set auto-close timer for 2 minutes (120000ms)
        const timer = setTimeout(() => {
          setNotifications((prev) =>
            prev.filter((notif) => notif.id !== newNotification.id)
          );
          timersRef.current.delete(newNotification.id);
        }, 120000); // 2 minutes

        timersRef.current.set(newNotification.id, timer);
      } catch (error) {
        // Silently handle errors
      }
    },
    [gvTalkAuth, playMessageNotif]
  );

  useAblyEventChannel(
    AblyMessageEvent.Reacted,
    onChatMessageReacted,
    chatChannel
  );

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current.clear();
    };
  }, []);

  // Function to remove a notification
  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((notif) => notif.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  return (
    <>
      <Audio controls ref={msgAudioRef}>
        <source type="audio/mp3" src={newMessageAudioURL} />
      </Audio>
      <Audio controls ref={audioRef}>
        <source src={audioURL} type="audio/mp3" />
      </Audio>
      {notifications.map((notification, index) => (
        <AlertMessage
          index={index}
          key={notification.id}
          showAlertMessage={true}
          body={notification.body}
          icon={notification.icon}
          chatId={notification.chatId}
          senderName={notification.senderName}
          senderRole={notification.senderRole}
          setShowAlertMessage={() => removeNotification(notification.id)}
        />
      ))}
    </>
  );
};
